import React, {useState} from 'react';
import styles from "../../../styles/editAnnoucement.module.scss";
import {
    FloatingLabelInput,
    FloatingLabelSelect,
    FloatingLabelTextArea
} from "../../elements/floatingLabelInput/FloatingLabelInput";
import {ANNOUNCEMENT_STATUS_OPTIONS, MEDIA_TYPE_OPTIONS, formatAnnoucementStatusAsPill} from "../../../utils/announcement.util";
import Select from "../../elements/select/Select";
import {Segmented, Spin, Upload} from "antd";
import {toast} from "react-toastify";
import TranslateButton from "../../elements/TranslateButton/TranslateButton";
import {useTranslateFields} from "../../../hooks/useTranslateFields";

const AddAnnoucementStep1 = ({ formData, updateFormData }) => {
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const { translating, translate } = useTranslateFields();

    const handleTranslate = async () => {
        if (!formData.displayName?.sr?.trim()) {
            toast.error('Popunite naslov na srpskom pre prevođenja');
            return;
        }

        const fields = { displayName: formData.displayName.sr };
        if (formData.displayDescription?.sr?.trim()) {
            fields.displayDescription = formData.displayDescription.sr;
        }
        if (formData.displayText?.sr?.trim()) {
            fields.displayText = formData.displayText.sr;
        }

        const result = await translate(fields);
        if (!result) return;

        // Merge hu+en into a single updateFormData call per field: updateFormData
        // closes over `formData` directly (not a functional setState updater), so
        // two calls in a row for the same field would have the second clobber the
        // first — each would merge onto the same stale pre-translate `formData`.
        updateFormData('displayName', {
            ...formData.displayName,
            hu: result.hu.displayName,
            en: result.en.displayName,
        });
        if (result.hu.displayDescription !== undefined || result.en.displayDescription !== undefined) {
            updateFormData('displayDescription', {
                ...formData.displayDescription,
                ...(result.hu.displayDescription !== undefined ? { hu: result.hu.displayDescription } : {}),
                ...(result.en.displayDescription !== undefined ? { en: result.en.displayDescription } : {}),
            });
        }
        if (result.hu.displayText !== undefined || result.en.displayText !== undefined) {
            updateFormData('displayText', {
                ...formData.displayText,
                ...(result.hu.displayText !== undefined ? { hu: result.hu.displayText.slice(0, 500) } : {}),
                ...(result.en.displayText !== undefined ? { en: result.en.displayText.slice(0, 500) } : {}),
            });
        }

        toast.success('Prevod je uspešno primenjen');
    };

    // Convert file to base64 data URL
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    // Handle image upload
    const handleImageUpload = async (file) => {
        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
            return false;
        }

        // Validate file size (5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
            return false;
        }

        setUploadingImage(true);
        try {
            // Convert to base64
            const base64 = await fileToBase64(file);

            // Update form data with base64 (will be uploaded to S3 on submit)
            updateFormData('imageUrl', base64);

            toast.success('Slika je uspešno učitana');
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Greška pri učitavanju slike');
        } finally {
            setUploadingImage(false);
        }

        return false; // Prevent default upload behavior
    };
    
    const handleDeleteImage = () => {
        updateFormData('imageUrl', '');
    };

    // Handle video upload
    const handleVideoUpload = async (file) => {
        // Validate file type
        if (file.type !== 'video/mp4') {
            toast.error('Dozvoljen je samo MP4 format');
            return false;
        }

        // Validate file size (50MB)
        const maxSize = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > maxSize) {
            toast.error('Video je prevelik. Maksimalna veličina je 50MB');
            return false;
        }

        setUploadingVideo(true);
        try {
            // Convert to base64
            const base64 = await fileToBase64(file);

            // Update form data with base64 (will be uploaded to S3 on submit)
            updateFormData('videoUrl', base64);

            toast.success('Video je uspešno učitan');
        } catch (error) {
            console.error('Error uploading video:', error);
            toast.error('Greška pri učitavanju videa');
        } finally {
            setUploadingVideo(false);
        }

        return false; // Prevent default upload behavior
    };

    const handleDeleteVideo = () => {
        updateFormData('videoUrl', '');
    };

    const handleMediaTypeChange = (value) => {
        updateFormData('mediaType', value);
        if (value === 'video') {
            updateFormData('imageUrl', undefined);
        } else {
            updateFormData('videoUrl', undefined);
        }
    };

    return (
        <div>
            {/* Osnovne informacije */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Osnovne informacije</h3>
                <div className={styles.formRowDouble}>
                    <FloatingLabelInput
                        label="Interni naziv"
                        value={formData.name}
                        onChange={(e) => updateFormData("name", e.target.value)}
                        placeholder="Objava za januar"
                    />
                    <Select
                        value={ANNOUNCEMENT_STATUS_OPTIONS.find(opt => opt.value === formData.status)}
                        onChange={(value) => updateFormData('status', value)}
                        items={ANNOUNCEMENT_STATUS_OPTIONS}
                        placeholder="Izaberi status"
                    />
                </div>
            </section>
    
            {/* Sadržaj objave */}
            <section className={styles.section}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 className={styles.sectionTitle}>Sadržaj objave</h3>
                    <TranslateButton onClick={handleTranslate} loading={translating} />
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.formGroupLeft}>
                        <FloatingLabelInput
                            label="Naslov"
                            value={formData.displayName?.sr}
                            onChange={(e) => updateFormData("displayName", { ...formData.displayName, sr: e.target.value })}
                            placeholder="Tvoja dnevna doza zdravlja"
                        />
                        <FloatingLabelInput
                            label="Kratak opis (opciono)"
                            value={formData.displayDescription?.sr}
                            onChange={(e) => updateFormData("displayDescription", { ...formData.displayDescription, sr: e.target.value })}
                        />
                    </div>
                    <div className={styles.formGroupRight}>
                        <FloatingLabelTextArea
                            label="Tekst objave (maks. 500 karaktera)"
                            value={formData.displayText?.sr}
                            onChange={(e) => updateFormData("displayText", { ...formData.displayText, sr: e.target.value.slice(0, 500) })}
                            rows={6}
                        />
                        <span style={{ display: 'block', textAlign: 'right', fontSize: '12px', color: (formData.displayText?.sr || '').length >= 500 ? '#c62828' : '#97a0af', marginTop: '4px' }}>
                            {(formData.displayText?.sr || '').length}/500
                        </span>
                    </div>
                </div>
            </section>

            {/* Mađarski prevod */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Sadržaj objave - mađarski prevod</h3>
                <div className={styles.formGrid}>
                    <div className={styles.formGroupLeft}>
                        <FloatingLabelInput
                            label="Naslov"
                            value={formData.displayName?.hu}
                            onChange={(e) => updateFormData("displayName", { ...formData.displayName, hu: e.target.value })}
                            placeholder="Tvoja dnevna doza zdravlja"
                        />
                        <FloatingLabelInput
                            label="Kratak opis (opciono)"
                            value={formData.displayDescription?.hu}
                            onChange={(e) => updateFormData("displayDescription", { ...formData.displayDescription, hu: e.target.value })}
                        />
                    </div>
                    <div className={styles.formGroupRight}>
                        <FloatingLabelTextArea
                            label="Tekst objave (maks. 500 karaktera)"
                            value={formData.displayText?.hu}
                            onChange={(e) => updateFormData("displayText", { ...formData.displayText, hu: e.target.value.slice(0, 500) })}
                            rows={6}
                        />
                        <span style={{ display: 'block', textAlign: 'right', fontSize: '12px', color: (formData.displayText?.hu || '').length >= 500 ? '#c62828' : '#97a0af', marginTop: '4px' }}>
                            {(formData.displayText?.hu || '').length}/500
                        </span>
                    </div>
                </div>
            </section>

            {/* Engleski prevod */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Sadržaj objave - engleski prevod</h3>
                <div className={styles.formGrid}>
                    <div className={styles.formGroupLeft}>
                        <FloatingLabelInput
                            label="Naslov"
                            value={formData.displayName?.en}
                            onChange={(e) => updateFormData("displayName", { ...formData.displayName, en: e.target.value })}
                            placeholder="Tvoja dnevna doza zdravlja"
                        />
                        <FloatingLabelInput
                            label="Kratak opis (opciono)"
                            value={formData.displayDescription?.en}
                            onChange={(e) => updateFormData("displayDescription", { ...formData.displayDescription, en: e.target.value })}
                        />
                    </div>
                    <div className={styles.formGroupRight}>
                        <FloatingLabelTextArea
                            label="Tekst objave (maks. 500 karaktera)"
                            value={formData.displayText?.en}
                            onChange={(e) => updateFormData("displayText", { ...formData.displayText, en: e.target.value.slice(0, 500) })}
                            rows={6}
                        />
                        <span style={{ display: 'block', textAlign: 'right', fontSize: '12px', color: (formData.displayText?.en || '').length >= 500 ? '#c62828' : '#97a0af', marginTop: '4px' }}>
                            {(formData.displayText?.en || '').length}/500
                        </span>
                    </div>
                </div>
            </section>
    
            {/* Podešavanje linka */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Podešavanje linka</h3>
                <div className={styles.formRowTriple}>
                    <FloatingLabelInput
                        label="Link"
                        value={formData.link}
                        onChange={(e) => updateFormData('link', e.target.value)}
                        placeholder="https://eliksirvitalnosti.com/"
                    />
                </div>
            </section>

            {/* Priloži fotografiju ili video */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Sadržaj (slika ili video)</h3>
                <Segmented
                    block
                    value={formData.mediaType || 'image'}
                    onChange={handleMediaTypeChange}
                    options={MEDIA_TYPE_OPTIONS}
                    style={{ marginBottom: 16 }}
                />
                {formData.mediaType === 'video' ? (
                    <div className={styles.photoUploadContainer}>
                        <div className={styles.previewWrapper}>
                            {formData.videoUrl ? (
                                <>
                                    <video
                                        src={formData.videoUrl}
                                        controls
                                        className={styles.imagePreview}
                                    />
                                    <button
                                        className={styles.deleteBtn}
                                        onClick={handleDeleteVideo}
                                        type="button"
                                        disabled={uploadingVideo}
                                    >
                                        <span className={styles.trashIcon}>🗑️</span>
                                    </button>
                                </>
                            ) : (
                                <img
                                    src="/logo.svg"
                                    alt="Placeholder"
                                    className={styles.imagePreview}
                                />
                            )}
                        </div>
                        <Upload
                            accept="video/mp4"
                            beforeUpload={handleVideoUpload}
                            showUploadList={false}
                            disabled={uploadingVideo}
                            className={styles.uploadBoxWrapper}
                        >
                            <div className={styles.uploadBox}>
                                <div className={styles.uploadContent}>
                                    <div className={styles.cloudIconContainer}>
                                        {uploadingVideo ? (
                                            <Spin />
                                        ) : (
                                            <span className={styles.cloudIcon}>☁️</span>
                                        )}
                                    </div>
                                    <p className={styles.uploadText}>
                                            <span className={styles.highlight}>
                                                {uploadingVideo ? 'Učitavanje...' : 'Kliknite za otpremanje videa'}
                                            </span> ili prevuci i ispusti
                                    </p>
                                    <small className={styles.fileTypes}>.MP4 (max. 50MB)</small>
                                </div>
                            </div>
                        </Upload>
                    </div>
                ) : (
                    <div className={styles.photoUploadContainer}>
                        <div className={styles.previewWrapper}>
                            {formData.imageUrl ? (
                                <>
                                    <img
                                        src={formData.imageUrl}
                                        alt="Preview"
                                        className={styles.imagePreview}
                                    />
                                    <button
                                        className={styles.deleteBtn}
                                        onClick={handleDeleteImage}
                                        type="button"
                                        disabled={uploadingImage}
                                    >
                                        <span className={styles.trashIcon}>🗑️</span>
                                    </button>
                                </>
                            ) : (
                                <img
                                    src="/logo.svg"
                                    alt="Placeholder"
                                    className={styles.imagePreview}
                                />
                            )}
                        </div>
                        <Upload
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            beforeUpload={handleImageUpload}
                            showUploadList={false}
                            disabled={uploadingImage}
                            className={styles.uploadBoxWrapper}
                        >
                            <div className={styles.uploadBox}>
                                <div className={styles.uploadContent}>
                                    <div className={styles.cloudIconContainer}>
                                        {uploadingImage ? (
                                            <Spin />
                                        ) : (
                                            <span className={styles.cloudIcon}>☁️</span>
                                        )}
                                    </div>
                                    <p className={styles.uploadText}>
                                            <span className={styles.highlight}>
                                                {uploadingImage ? 'Učitavanje...' : 'Kliknite za otpremanje slike'}
                                            </span> ili prevuci i ispusti
                                    </p>
                                    <small className={styles.fileTypes}>.JPG, .PNG, .WebP (max. 5MB)</small>
                                </div>
                            </div>
                        </Upload>
                    </div>
                )}
            </section>
        </div>
    );
};

export default AddAnnoucementStep1;